// JSN Supabase Database Types

export type UserRole = 'user' | 'admin';
export type MembershipTier = 'free' | 'premium';
export type PhotoStatus = 'none' | 'pending' | 'approved' | 'rejected';
export type AccountStatus = 'active' | 'suspended' | 'deleted' | 'deactivated';
export type ProfileStatus = 'online' | 'new' | 'verified' | 'premium' | 'offline';
export type ReportStatus = 'pending' | 'under_review' | 'reviewed' | 'resolved' | 'dismissed' | 'warning_sent' | 'action_taken';

export interface UserProfile {
  id: string;
  email: string;
  displayName: string;
  firstName?: string | null;
  lastName?: string | null;
  role: UserRole;
  membershipTier: MembershipTier;
  membershipPlan?: string | null;
  membershipStartDate?: string | null;
  membershipExpiryDate?: string | null;
  avatarInitials: string;
  avatarColor: string;
  joinedDate: string;
  emailVerified: boolean;
  emailVerifiedAt?: string | null;
  accountStatus: AccountStatus;
  stripeCustomerId?: string | null;
  phoneNumber?: string | null;
  phoneVerified?: boolean;
  phoneVerifiedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  // Enforcement fields
  enforcementReason?: string | null;
  enforcementType?: string | null;
  enforcementDate?: string | null;
  enforcementNotes?: string | null;
  deactivationDeleteAfter?: string | null;
  correctedProfilePendingReview?: boolean;
  identityReviewed?: boolean;
  identityReviewedAt?: string | null;
  identityReviewedBy?: string | null;
}

export interface MemberProfile {
  id: string;
  userId: string;
  displayName: string;
  firstName?: string | null;
  lastName?: string | null;
  age: number;
  gender: string;
  usState: string;
  kenyanCounty: string;
  faith: string;
  intention: string;
  interests: string[];
  bio: string;
  profileStatus: ProfileStatus;
  avatarInitials: string;
  avatarColor: string;
  joinedDate: string;
  lastActive: string;
  profileComplete: number;
  photoUrl?: string | null;
  photoStatus: PhotoStatus;
  photoRejectionReason?: string | null;
  isSeed: boolean;
  adminLastEditedAt?: string | null;
  adminLastEditedBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Like {
  id: string;
  likerUserId: string;
  likedProfileId: string;
  createdAt: string;
}

export interface Pass {
  id: string;
  passerUserId: string;
  passedProfileId: string;
  createdAt: string;
}

export interface Block {
  id: string;
  blockerUserId: string;
  blockedProfileId: string;
  createdAt: string;
}

export interface Report {
  id: string;
  reporterUserId: string;
  reportedProfileId: string;
  reason: string;
  details: string;
  status: ReportStatus;
  adminNote?: string | null;
  createdAt: string;
  updatedAt?: string;
}

export interface AdminNote {
  id: string;
  targetUserId: string;
  adminUserId: string;
  note: string;
  createdAt: string;
  updatedAt: string;
}

export interface AuditLogEntry {
  id: string;
  adminUserId: string;
  targetUserId?: string | null;
  action: string;
  details?: string | null;
  reason?: string | null;
  createdAt: string;
}

export interface Conversation {
  id: string;
  participantOneId: string;
  participantTwoId: string;
  lastMessage?: string | null;
  lastMessageTime?: string | null;
  unreadCountOne: number;
  unreadCountTwo: number;
  createdAt: string;
  updatedAt: string;
}

export interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  text: string;
  isRead: boolean;
  createdAt: string;
}

export interface PremiumSubscription {
  id: string;
  userId: string;
  stripeSessionId?: string | null;
  stripePaymentIntentId?: string | null;
  stripeCustomerId?: string | null;
  plan: string;
  amountCents: number;
  currency: string;
  paymentStatus: string;
  startDate?: string | null;
  endDate?: string | null;
  createdAt: string;
  updatedAt: string;
}

// Preview Profile — no Supabase Auth account, profile_type = 'preview'
export interface PreviewProfile {
  id: string;
  displayName: string;
  age: number;
  gender: string;
  usState: string;
  kenyanCounty: string;
  faith: string;
  intention: string;
  interests: string[];
  bio: string;
  avatarInitials: string;
  avatarColor: string;
  photoUrl?: string | null;
  profileType: 'preview';
  isVisible: boolean;
  createdAt: string;
  updatedAt: string;
}

// DB row → camelCase converters
export function toUserProfile(row: Record<string, unknown>): UserProfile {
  return {
    id: row.id as string,
    email: row.email as string,
    displayName: row.display_name as string,
    firstName: row.first_name as string | null ?? null,
    lastName: row.last_name as string | null ?? null,
    role: row.role as UserRole,
    membershipTier: row.membership_tier as MembershipTier,
    membershipPlan: row.membership_plan as string | null,
    membershipStartDate: row.membership_start_date as string | null,
    membershipExpiryDate: row.membership_expiry_date as string | null,
    avatarInitials: row.avatar_initials as string,
    avatarColor: row.avatar_color as string,
    joinedDate: row.joined_date as string,
    emailVerified: row.email_verified as boolean,
    emailVerifiedAt: row.email_verified_at as string | null ?? null,
    accountStatus: row.account_status as AccountStatus,
    stripeCustomerId: row.stripe_customer_id as string | null,
    phoneNumber: row.phone_number as string | null,
    phoneVerified: row.phone_verified as boolean ?? false,
    phoneVerifiedAt: row.phone_verified_at as string | null,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
    enforcementReason: row.enforcement_reason as string | null ?? null,
    enforcementType: row.enforcement_type as string | null ?? null,
    enforcementDate: row.enforcement_date as string | null ?? null,
    enforcementNotes: row.enforcement_notes as string | null ?? null,
    deactivationDeleteAfter: row.deactivation_delete_after as string | null ?? null,
    correctedProfilePendingReview: (row.corrected_profile_pending_review as boolean) ?? false,
    identityReviewed: (row.identity_reviewed as boolean) ?? false,
    identityReviewedAt: row.identity_reviewed_at as string | null ?? null,
    identityReviewedBy: row.identity_reviewed_by as string | null ?? null,
  };
}

export function toMemberProfile(row: Record<string, unknown>): MemberProfile {
  return {
    id: row.id as string,
    userId: row.user_id as string,
    displayName: row.display_name as string,
    firstName: row.first_name as string | null ?? null,
    lastName: row.last_name as string | null ?? null,
    age: row.age as number,
    gender: row.gender as string,
    usState: row.us_state as string,
    kenyanCounty: row.kenyan_county as string,
    faith: row.faith as string,
    intention: row.intention as string,
    interests: (row.interests as string[]) || [],
    bio: row.bio as string,
    profileStatus: row.profile_status as ProfileStatus,
    avatarInitials: row.avatar_initials as string,
    avatarColor: row.avatar_color as string,
    joinedDate: row.joined_date as string,
    lastActive: row.last_active as string,
    profileComplete: row.profile_complete as number,
    photoUrl: row.photo_url as string | null,
    photoStatus: row.photo_status as PhotoStatus,
    photoRejectionReason: row.photo_rejection_reason as string | null,
    isSeed: row.is_seed as boolean,
    adminLastEditedAt: row.admin_last_edited_at as string | null ?? null,
    adminLastEditedBy: row.admin_last_edited_by as string | null ?? null,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

export function toConversation(row: Record<string, unknown>): Conversation {
  return {
    id: row.id as string,
    participantOneId: row.participant_one_id as string,
    participantTwoId: row.participant_two_id as string,
    lastMessage: row.last_message as string | null,
    lastMessageTime: row.last_message_time as string | null,
    unreadCountOne: row.unread_count_one as number,
    unreadCountTwo: row.unread_count_two as number,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

export function toMessage(row: Record<string, unknown>): Message {
  return {
    id: row.id as string,
    conversationId: row.conversation_id as string,
    senderId: row.sender_id as string,
    text: row.text as string,
    isRead: row.is_read as boolean,
    createdAt: row.created_at as string,
  };
}

export function toReport(row: Record<string, unknown>): Report {
  return {
    id: row.id as string,
    reporterUserId: row.reporter_user_id as string,
    reportedProfileId: row.reported_profile_id as string,
    reason: row.reason as string,
    details: row.details as string,
    status: row.status as ReportStatus,
    adminNote: row.admin_note as string | null,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

export function toAdminNote(row: Record<string, unknown>): AdminNote {
  return {
    id: row.id as string,
    targetUserId: row.target_user_id as string,
    adminUserId: row.admin_user_id as string,
    note: row.note as string,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

export function toAuditLogEntry(row: Record<string, unknown>): AuditLogEntry {
  return {
    id: row.id as string,
    adminUserId: row.admin_user_id as string,
    targetUserId: row.target_user_id as string | null,
    action: row.action as string,
    details: row.details as string | null,
    reason: row.reason as string | null,
    createdAt: row.created_at as string,
  };
}

export function toPreviewProfile(row: Record<string, unknown>): PreviewProfile {
  return {
    id: row.id as string,
    displayName: row.display_name as string,
    age: row.age as number,
    gender: row.gender as string,
    usState: row.us_state as string,
    kenyanCounty: row.kenyan_county as string,
    faith: row.faith as string,
    intention: row.intention as string,
    interests: (row.interests as string[]) || [],
    bio: row.bio as string,
    avatarInitials: row.avatar_initials as string,
    avatarColor: row.avatar_color as string,
    photoUrl: row.photo_url as string | null,
    profileType: 'preview',
    isVisible: row.is_visible as boolean,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

// ── Issue Report ──────────────────────────────────────────────────────────────

export interface IssueReport {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  area: string;
  issueType: string;
  deviceType: string;
  otherDetails: string | null;
  status: string;
  adminNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

export function toIssueReport(row: Record<string, unknown>): IssueReport {
  return {
    id: row.id as string,
    userId: row.user_id as string,
    userEmail: row.user_email as string,
    userName: row.user_name as string,
    area: row.area as string,
    issueType: row.issue_type as string,
    deviceType: row.device_type as string,
    otherDetails: row.other_details as string | null,
    status: row.status as string,
    adminNotes: row.admin_notes as string | null,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}
